import { lazy, Suspense, useCallback, useEffect, useReducer, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api } from "../api";
import { warnIfShared } from "../grantwarn";
import { ConfirmDialog, ConnIcon, ErrorBanner, Skeleton } from "../components";
import { preloadTranscript } from "../transcriptCache";
import { filterAppName, isValidAppName } from "../appname";
import { workingReducer, chatDotState } from "../chat";

// The label a conversation shows: its title if set, else the default. The slug
// (name) is an internal handle and is never shown.
const chatLabel = (chat) => (chat && chat.title) || "New chat";

const sameDay = (a, b) => a.toDateString() === b.toDateString();
const monthDay = (d) => d.toLocaleDateString([], { month: "short", day: "numeric" });

// The sidebar row's timestamp: the last-used time as a clock time if today,
// "Yesterday", or a short date; then ", expires <date>" unless it is pinned (a
// pinned chat never expires, so no expiry is shown).
const chatWhen = (chat) => {
  const base = chat.last_active_at
    ? new Date(chat.last_active_at * 1000)
    : chat.created_at
      ? new Date(chat.created_at)
      : null;
  if (!base) return "";
  const now = new Date();
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  let when;
  if (sameDay(base, now)) {
    when = base.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" }).toLowerCase();
  } else if (sameDay(base, yesterday)) {
    when = "Yesterday";
  } else {
    when = monthDay(base);
  }
  if (chat.expires_at) {
    when += ", expires " + monthDay(new Date(chat.expires_at * 1000));
  }
  return when;
};

// Sidebar order: pinned conversations float to the top, newest first within each
// group. Shared by the list load and the optimistic insert on create.
const sortChats = (list) =>
  [...list].sort(
    (a, b) =>
      (b.pinned ? 1 : 0) - (a.pinned ? 1 : 0) ||
      (b.created_at || "").localeCompare(a.created_at || ""),
  );

// The assistant is the heavy child, so it loads on demand like it does in the
// app view.
const AppAssistant = lazy(() => import("./AppAssistant"));

// A conversation lives on its own page, not in the Apps grid: a sidebar of past
// chats on the left, the active one filling the rest. This is the ChatGPT-shaped
// surface -- a chat is the base primitive, an app is a chat that also serves, so
// chats get first-class real estate rather than being an app variant.
// The messaging-bubble glyph, matching the assistant's empty-state icon so the
// New chat button and the conversation welcome read as the same family.
const NewChatIcon = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M21 15a2 2 0 0 1-2 2H8l-4 4V5a2 2 0 0 1 2-2h13a2 2 0 0 1 2 2z" />
    <path d="M8.5 9.5h9M8.5 12.5h5" />
  </svg>
);

// A sidebar/panel glyph for the collapse (desktop) / drawer (mobile) toggle.
const SidebarToggleIcon = () => (
  <svg viewBox="0 0 16 16" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="2" y="3" width="12" height="10" rx="1.5" />
    <path d="M6 3v10" />
  </svg>
);

const KebabIcon = () => (
  <svg viewBox="0 0 16 16" width="16" height="16" fill="currentColor" aria-hidden="true">
    <circle cx="3" cy="8" r="1.4" />
    <circle cx="8" cy="8" r="1.4" />
    <circle cx="13" cy="8" r="1.4" />
  </svg>
);

// Menu-item glyphs, drawn in the same 16px stroked style as the app-detail
// actions menu so the two menus read as one family: a chain (connections), a
// restore-clock (snapshots), a pencil (rename), a fork (convert), a bin (delete).
const mi = (paths) => () => (
  <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    {paths}
  </svg>
);
const ConnectionsMenuIcon = mi(<><path d="M10 4.5h2a3.5 3.5 0 0 1 0 7h-2M6 4.5H4a3.5 3.5 0 0 0 0 7h2" /><path d="M5.5 8h5" /></>);
const RenameMenuIcon = mi(<path d="M11.3 2.2l2.5 2.5L6 12.5l-3.2.7.7-3.2z" />);
const ConvertMenuIcon = mi(<><circle cx="4.5" cy="4" r="1.6" /><circle cx="11.5" cy="4" r="1.6" /><circle cx="8" cy="12.5" r="1.6" /><path d="M4.5 5.6v1.4a2 2 0 0 0 2 2H8m3.5-3.4v1.4a2 2 0 0 1-2 2H8m0 0v1.9" /></>);
const DeleteMenuIcon = mi(<><path d="M3 4.5h10" /><path d="M6.5 4.5V3h3v1.5" /><path d="M4.5 4.5l.6 8.5a1 1 0 0 0 1 .9h3.8a1 1 0 0 0 1-.9l.6-8.5" /></>);
const DownloadMenuIcon = mi(<><path d="M8 2.5v7" /><path d="M4.8 6.7 8 9.9l3.2-3.2" /><path d="M3 12.5h10" /></>);

// The eye from the app workspace's preview toggle, so the two read as the same
// control.
const EyeIcon = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7z" />
    <circle cx="12" cy="12" r="2.5" />
  </svg>
);

// A thumbtack, filled when the conversation is pinned. Pinning exempts a chat
// from the idle-expiration sweep.
const PinIcon = ({ filled }) => (
  <svg viewBox="0 0 16 16" width="14" height="14" fill={filled ? "currentColor" : "none"} stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M9.5 2.5l4 4-2 .5-2.5 2.5-.5 3-1-1-2.5-2.5-1-1 3-.5L9 4.5z" />
    <path d="M6 10l-3 3" />
  </svg>
);

// The sidebar row for one conversation. The whole row opens the chat; on hover
// (or when pinned) it reveals a pin toggle and a rename pencil, the way the file
// browser reveals per-row actions. Not a <button> -- it holds buttons.
const ChatRow = ({ chat, active, working, onOpen, onRename, onTogglePin, onConvert, onDelete }) => (
  <div className={"chats-row" + (active ? " on" : "") + (chat.pinned ? " pinned" : "")}>
    <button type="button" className="chats-row-main" onClick={() => onOpen(chat.name)} title={chatLabel(chat)}>
      <span className="chats-row-titleline">
        <span className={"chats-row-dot " + chatDotState(chat, working)} title={working ? "Working" : "Idle"} aria-hidden="true" />
        <span className="chats-row-title">{chatLabel(chat)}</span>
      </span>
      <span className="chats-row-date">{chatWhen(chat)}</span>
    </button>
    <span className="chats-row-actions">
      <button type="button" className="chats-row-act chats-row-pin" title={chat.pinned ? "Unpin" : "Pin"} aria-label={chat.pinned ? "Unpin conversation" : "Pin conversation"} onClick={() => onTogglePin(chat)}>
        <PinIcon filled={chat.pinned} />
      </button>
      <ChatMenu
        compact
        fixed
        name={chat.name}
        pinned={chat.pinned}
        onTogglePin={() => onTogglePin(chat)}
        onRename={() => onRename(chat)}
        onConvert={() => onConvert(chat)}
        onDelete={() => onDelete(chat)}
      />
    </span>
  </div>
);

// A small check, shown on a granted connection row in the submenu.
const CheckIcon = () => (
  <svg viewBox="0 0 16 16" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M3.5 8.5l3 3 6-7" />
  </svg>
);

// The conversation actions menu (three dots): pin, a Connections submenu that
// grants/revokes each connection inline (no modal), snapshots, rename, convert,
// delete. Used both at the top-right of the open conversation and per sidebar row.
const ChatMenu = ({ name, onRename, onConvert, onDelete, onTogglePin, pinned, busy, compact = false, fixed = false }) => {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState(null);
  const [submenuOpen, setSubmenuOpen] = useState(false); // the connections flyout
  const [conns, setConns] = useState(null); // {granted, available}
  const [hasFiles, setHasFiles] = useState(null); // null until the menu opens; gates "Download files"
  const ref = useRef(null);
  const btnRef = useRef(null);

  const loadConns = useCallback(async () => {
    try {
      setConns(await api.get(`/api/apps/${encodeURIComponent(name)}/connections`));
    } catch {
      setConns({ granted: [], available: [] });
    }
  }, [name]);
  // Keep this menu's checks in sync when a connection is toggled from another
  // surface (the composer popover, the empty-state chips, the other menu).
  useEffect(() => {
    const onChange = (e) => {
      if (e.detail === name) loadConns();
    };
    window.addEventListener("hostit:conn", onChange);
    return () => window.removeEventListener("hostit:conn", onChange);
  }, [name, loadConns]);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    // A fixed menu is detached from the row, so close it if the list scrolls or
    // the window resizes rather than leaving it floating in the wrong place.
    const onMove = () => fixed && setOpen(false);
    window.addEventListener("scroll", onMove, true);
    window.addEventListener("resize", onMove);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      window.removeEventListener("scroll", onMove, true);
      window.removeEventListener("resize", onMove);
    };
  }, [open, fixed]);
  // Close the connections flyout whenever the menu closes.
  useEffect(() => {
    if (!open) setSubmenuOpen(false);
  }, [open]);
  // On open, learn whether the chat has anything to download, so "Download
  // files" is disabled (not an empty archive) when there is nothing yet.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    api
      .get(`/api/apps/${encodeURIComponent(name)}/preview-status`)
      .then((r) => !cancelled && setHasFiles(!!r.has_files))
      .catch(() => !cancelled && setHasFiles(null));
    return () => {
      cancelled = true;
    };
  }, [open, name]);

  const toggle = () => {
    // A row menu lives inside the scrollable sidebar, which would clip a normal
    // absolute dropdown; measure the trigger and position the menu with fixed
    // coords so it escapes the clip.
    if (!open && fixed && btnRef.current) {
      const r = btnRef.current.getBoundingClientRect();
      setPos({ top: r.bottom + 4, right: Math.max(8, window.innerWidth - r.right) });
    }
    setOpen((o) => !o);
  };
  const pick = (fn) => () => {
    setOpen(false);
    fn();
  };
  // Hovering (or tapping) the Connections item opens its flyout; hovering any
  // other item closes it, the way nested menus behave. Open-only, not toggle, so
  // a tap's synthetic mouseenter+click on mobile does not open-then-close it.
  const openSub = () => {
    if (!submenuOpen) {
      setSubmenuOpen(true);
      loadConns();
    }
  };
  const closeSub = () => setSubmenuOpen(false);
  const emitConnChange = () => window.dispatchEvent(new CustomEvent("hostit:conn", { detail: name }));
  const toggleConn = async (c) => {
    const path = `/api/apps/${encodeURIComponent(name)}/connections/${encodeURIComponent(c.slug)}`;
    // Flip it locally at once so the check toggles instantly, with no dim or
    // flicker; the request runs in the background and a reconcile follows.
    setConns((cur) => {
      if (!cur) return cur;
      const granted = (cur.granted || []).filter((x) => x.slug !== c.slug);
      const available = (cur.available || []).filter((x) => x.slug !== c.slug);
      if (c.granted) available.push(c);
      else granted.push(c);
      return { granted, available };
    });
    try {
      if (c.granted) await api.del(path);
      else {
        await api.put(path, {});
        warnIfShared(name, c.label || c.slug);
      }
    } catch {
      // ignore; the reconcile below (or the next open) repairs the state
    }
    emitConnChange();
    loadConns();
  };

  const connList = conns
    ? [
        ...(conns.granted || []).map((c) => ({ ...c, granted: true })),
        ...(conns.available || []).map((c) => ({ ...c, granted: false })),
      ].sort((a, b) => (a.label || a.slug).localeCompare(b.label || b.slug))
    : [];
  const allGranted = connList.length > 0 && connList.every((c) => c.granted);
  // "All connections": grant every one when any is missing, else clear them all.
  const toggleAll = async () => {
    const grantAll = !allGranted;
    setConns((cur) => {
      if (!cur) return cur;
      const items = [...(cur.granted || []), ...(cur.available || [])];
      return grantAll ? { granted: items, available: [] } : { granted: [], available: items };
    });
    try {
      for (const c of connList) {
        const path = `/api/apps/${encodeURIComponent(name)}/connections/${encodeURIComponent(c.slug)}`;
        if (grantAll && !c.granted) await api.put(path, {});
        else if (!grantAll && c.granted) await api.del(path);
      }
      if (grantAll) warnIfShared(name);
    } catch {
      // reconcile on reload
    }
    emitConnChange();
    loadConns();
  };

  return (
    <div className="chats-menu" ref={ref}>
      <button ref={btnRef} type="button" className={compact ? "chats-row-act" : "btn btn-icon"} onClick={toggle} disabled={busy} aria-haspopup="menu" aria-expanded={open} title="Conversation actions" aria-label="Conversation actions">
        <KebabIcon />
      </button>
      {open && (
        <div className={"menu-items" + (fixed ? " menu-fixed" : "")} style={fixed && pos ? { top: pos.top, right: pos.right } : undefined} role="menu">
          <button type="button" role="menuitem" onClick={pick(onTogglePin)} onMouseEnter={closeSub}><span className="menu-ico" aria-hidden="true"><PinIcon filled={pinned} /></span>{pinned ? "Unpin" : "Pin"}</button>
          <button type="button" role="menuitem" className={"menu-hassub" + (submenuOpen ? " on" : "")} onClick={openSub} onMouseEnter={openSub} aria-haspopup="menu" aria-expanded={submenuOpen}><span className="menu-ico" aria-hidden="true"><ConnectionsMenuIcon /></span>Connections<span className="menu-caret" aria-hidden="true">›</span></button>
          {hasFiles === false ? (
            <button type="button" role="menuitem" className="menu-item-disabled" disabled title="Nothing to download yet" onMouseEnter={closeSub}><span className="menu-ico" aria-hidden="true"><DownloadMenuIcon /></span>Download files</button>
          ) : (
            <a role="menuitem" href={`/api/apps/${encodeURIComponent(name)}/export`} download onClick={() => setOpen(false)} onMouseEnter={closeSub}><span className="menu-ico" aria-hidden="true"><DownloadMenuIcon /></span>Download files</a>
          )}
          <button type="button" role="menuitem" onClick={pick(onRename)} onMouseEnter={closeSub}><span className="menu-ico" aria-hidden="true"><RenameMenuIcon /></span>Rename</button>
          <button type="button" role="menuitem" onClick={pick(onConvert)} onMouseEnter={closeSub}><span className="menu-ico" aria-hidden="true"><ConvertMenuIcon /></span>Convert to app</button>
          <div className="menu-sep" />
          <button type="button" className="menu-item-danger" role="menuitem" onClick={pick(onDelete)} onMouseEnter={closeSub}><span className="menu-ico" aria-hidden="true"><DeleteMenuIcon /></span>Delete chat</button>
          {submenuOpen && (
            <div className={"menu-items menu-flyout" + (compact ? " menu-flyout-right" : "")} role="menu">
              <div className="menu-conns-list">
                {conns === null ? (
                  <div className="menu-conns-empty">Loading...</div>
                ) : connList.length === 0 ? (
                  <div className="menu-conns-empty">No connections yet. Add them on the Connections page.</div>
                ) : (
                  <>
                    <button type="button" role="menuitemcheckbox" aria-checked={allGranted} className={"menu-conn" + (allGranted ? " on" : "")} onClick={toggleAll}>
                      <span className="menu-conn-check" aria-hidden="true">{allGranted ? <CheckIcon /> : null}</span>
                      <span className="menu-conn-label">All connections</span>
                    </button>
                    <div className="menu-sep" />
                    {connList.map((c) => (
                      <button key={c.slug} type="button" role="menuitemcheckbox" aria-checked={c.granted} className={"menu-conn" + (c.granted ? " on" : "")} onClick={() => toggleConn(c)}>
                        <span className="menu-conn-check" aria-hidden="true">{c.granted ? <CheckIcon /> : null}</span>
                        <ConnIcon url={c.icon_url} className="menu-conn-icon" />
                        <span className="menu-conn-label">{c.label || c.slug}</span>
                        <span className="menu-conn-provider">{c.provider_label}</span>
                      </button>
                    ))}
                  </>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// A plain modal shell for the connections and rename dialogs. Kept local so the
// page does not depend on a shared component that does not exist.
const Sheet = ({ title, wide, onClose, children }) => {
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);
  return (
  <div className="modal-backdrop" role="dialog" aria-modal="true" onMouseDown={onClose}>
    <div className={"card modal modal-sheet chats-sheet" + (wide ? " modal-wider" : "")} onMouseDown={(e) => e.stopPropagation()}>
      <button type="button" className="modal-x" onClick={onClose} title="Close" aria-label="Close">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true"><path d="M6 6l12 12M18 6 6 18" /></svg>
      </button>
      {title && <h2>{title}</h2>}
      {children}
    </div>
  </div>
  );
};

const Chats = ({ account, refreshAccount }) => {
  const { name } = useParams();
  const navigate = useNavigate();
  const [chats, setChats] = useState(null);
  const activeChat = name ? (chats || []).find((c) => c.name === name) : null;
  const active = activeChat ? name : null;
  const [error, setError] = useState("");
  const [creating, setCreating] = useState(false);
  const [renaming, setRenaming] = useState(false);
  const [renameValue, setRenameValue] = useState("");
  const [renameTarget, setRenameTarget] = useState(null);
  const [converting, setConverting] = useState(false);
  const [activeBusy, setActiveBusy] = useState(false); // the open conversation's live running state, for its sidebar dot
  // Which chats are "doing something", for the sidebar dot -- ANY chat, not just
  // the open one. A working chat stays marked after you navigate away from it.
  const [workingNames, dispatchWorking] = useReducer(workingReducer, undefined, () => new Set());
  const activeRef = useRef(active);
  activeRef.current = active;
  const [sidebarW, setSidebarW] = useState(() => {
    try {
      const v = Number(localStorage.getItem("chats-sidebar-w"));
      if (v >= 200 && v <= 480) return v;
    } catch {
      // ignore; use the default
    }
    return 260;
  });
  const belowRef = useRef(null);
  const resizingRef = useRef(false);
  const widthRef = useRef(sidebarW);
  const [previewFrac, setPreviewFrac] = useState(() => {
    try {
      const v = Number(localStorage.getItem("chats-preview-frac"));
      if (v >= 0.2 && v <= 0.8) return v;
    } catch {
      // ignore; use the default
    }
    return 0.5;
  });
  const bodyRef = useRef(null);
  const previewResizingRef = useRef(false);
  const previewFracRef = useRef(previewFrac);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [busy, setBusy] = useState(false);
  const [previewReady, setPreviewReady] = useState(false);
  const [previewOn, setPreviewOn] = useState(true);
  // Small screens show one pane at a time: the sidebar is a slide-out drawer over
  // the conversation, opened from the header and closed on selection. On a wide
  // screen the same toggle collapses the sidebar in place instead.
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const [showConvert, setShowConvert] = useState(false);
  const [convertName, setConvertName] = useState("");
  const [previewNonce, setPreviewNonce] = useState(0);
  // One toggle, two behaviours by width, decided at click time (not render) so it
  // never depends on a stale viewport read: a phone opens the drawer, a desktop
  // collapses the sidebar.
  const toggleSidebar = () =>
    window.matchMedia("(max-width: 720px)").matches ? setDrawerOpen((o) => !o) : setCollapsed((c) => !c);

  // Touch gestures for the small-screen drawer: a swipe from the very left edge
  // opens it, a swipe left (anywhere) closes it. Tracked from the page container
  // so the whole conversation is a valid start surface. Ignored on wide screens,
  // where the sidebar is a column, not a drawer.
  const touchRef = useRef(null);
  const onTouchStart = (e) => {
    const t = e.touches[0];
    touchRef.current = { x: t.clientX, y: t.clientY, edge: t.clientX <= 28 };
  };
  const onTouchEnd = (e) => {
    const start = touchRef.current;
    touchRef.current = null;
    if (!start || !window.matchMedia("(max-width: 720px)").matches) return;
    const t = e.changedTouches[0];
    const dx = t.clientX - start.x;
    const dy = t.clientY - start.y;
    // A real horizontal swipe: far enough sideways, and more sideways than up/down.
    if (Math.abs(dx) < 45 || Math.abs(dy) > Math.abs(dx)) return;
    if (!drawerOpen && start.edge && dx > 0) setDrawerOpen(true);
    else if (drawerOpen && dx < 0) setDrawerOpen(false);
  };

  // Chats have their own per-user limit, separate from apps. Gate on the live
  // list length (immediate) against the effective chat limit from the account.
  const atLimit = Array.isArray(chats) && chats.length >= account.usage.chat_limit;

  const load = useCallback(async () => {
    try {
      const all = await api.get("/api/apps");
      // A chat is an app whose kind is "chat"; the Apps grid hides them and this
      // page shows only them. Pinned chats float to the top (that is what pinning
      // one in the sidebar is for); newest first within each group.
      const only = sortChats(all.filter((a) => a.kind === "chat"));
      setChats(only);
      setError("");
      // Warm the cache for the top few conversations so switching to them is
      // instant (no transcript flicker). Background, best-effort.
      only.slice(0, 3).forEach((c) => preloadTranscript(c.name));
    } catch (err) {
      setError(err.message);
      setChats([]);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);
  // A fresh server list reconciles the background chats' working state.
  const chatsRef = useRef([]);
  useEffect(() => {
    chatsRef.current = chats || [];
    dispatchWorking({ type: "load", chats: chats || [], active: activeRef.current });
  }, [chats]);
  // The open chat's live busy state owns the active row's dot.
  useEffect(() => {
    dispatchWorking({ type: "active", active, activeBusy });
  }, [active, activeBusy]);
  // Navigating refreshes the list so a chat you left (or return to) shows its
  // real state -- no continuous polling.
  useEffect(() => {
    if (active) load();
  }, [active, load]);
  // A new chat's title is written by a short background summary a few seconds
  // AFTER its first turn ends, so neither the turn finishing nor a one-off
  // reload catches it. When the open chat's turn just finished and it is still
  // untitled, poll briefly until the title lands (it usually does within a few
  // seconds; give up after ~24s so a chat that never gets one does not poll on).
  const wasActiveBusy = useRef(false);
  useEffect(() => {
    const justFinished = wasActiveBusy.current && !activeBusy;
    wasActiveBusy.current = activeBusy;
    if (!active || !justFinished) return;
    const chat = chatsRef.current.find((c) => c.name === active);
    if (chat && chat.title && chat.title !== "New chat") return;
    let tries = 0;
    const timer = setInterval(async () => {
      tries += 1;
      await load();
      const c = chatsRef.current.find((x) => x.name === active);
      if ((c && c.title && c.title !== "New chat") || tries >= 8) clearInterval(timer);
    }, 3000);
    return () => clearInterval(timer);
  }, [active, activeBusy, load]);

  // Drag-to-resize the sidebar. The handle sets a width variable on .chats-below;
  // the collapsed/mobile CSS overrides the column entirely so it is unaffected.
  const startResize = (e) => {
    e.preventDefault();
    resizingRef.current = true;
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
  };
  useEffect(() => {
    const onMove = (e) => {
      if (!resizingRef.current || !belowRef.current) return;
      const rect = belowRef.current.getBoundingClientRect();
      const w = Math.max(200, Math.min(480, Math.round(e.clientX - rect.left)));
      widthRef.current = w;
      setSidebarW(w);
    };
    const onUp = () => {
      if (!resizingRef.current) return;
      resizingRef.current = false;
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
      try {
        localStorage.setItem("chats-sidebar-w", String(widthRef.current));
      } catch {
        // ignore; the width just is not remembered
      }
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
  }, []);

  // Drag-to-resize the conversation/preview split, the same way as the sidebar.
  const startPreviewResize = (e) => {
    e.preventDefault();
    previewResizingRef.current = true;
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
    // While dragging, the preview iframe must not swallow the mouse: in Firefox,
    // moving the pointer over an iframe routes events to it, so mousemove stops
    // (cannot drag past it) and mouseup is missed (the drag sticks). This class
    // sets pointer-events:none on the frame for the duration of the drag.
    document.body.classList.add("chats-resizing");
  };
  useEffect(() => {
    const onMove = (e) => {
      if (!previewResizingRef.current || !bodyRef.current) return;
      const rect = bodyRef.current.getBoundingClientRect();
      const f = Math.max(0.2, Math.min(0.8, (e.clientX - rect.left) / rect.width));
      previewFracRef.current = f;
      setPreviewFrac(f);
    };
    const onUp = () => {
      if (!previewResizingRef.current) return;
      previewResizingRef.current = false;
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
      document.body.classList.remove("chats-resizing");
      try {
        localStorage.setItem("chats-preview-frac", String(previewFracRef.current));
      } catch {
        // ignore; the split just is not remembered
      }
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
  }, []);


  const open = (n) => {
    setDrawerOpen(false);
    navigate(`/chats/${encodeURIComponent(n)}`);
  };

  // Does the active conversation have a built static site to preview? Checked on
  // open and after each turn (a turn may create the first asset).
  const checkPreview = useCallback(async (chatName) => {
    if (!chatName) {
      setPreviewReady(false);
      return;
    }
    try {
      const r = await api.get(`/api/apps/${encodeURIComponent(chatName)}/preview-status`);
      setPreviewReady(!!r.ready);
    } catch {
      setPreviewReady(false);
    }
  }, []);
  useEffect(() => {
    checkPreview(name);
  }, [name, checkPreview]);

  const createChat = async () => {
    if (creating || atLimit) return;
    setCreating(true);
    setError("");
    try {
      const generated = "chat-" + Math.random().toString(36).slice(2, 8);
      const res = await api.post("/api/apps", { name: generated, kind: "chat" });
      // Open it at once: a chat creates no resources, so there is nothing to wait
      // for. Insert it optimistically and refresh the list in the background
      // rather than blocking the open on a full re-fetch.
      setChats((prev) => sortChats([res, ...(prev || [])]));
      open(res.name);
      refreshAccount();
      load();
    } catch (err) {
      setError(err.status === 409 ? "Could not start a conversation, please try again" : err.message);
    } finally {
      setCreating(false);
    }
  };

  // openRename targets a specific conversation (the active one from the header,
  // or any row from the sidebar), so a row's pencil renames that row.
  const openRename = (chat) => {
    setRenameTarget(chat.name);
    setRenameValue(chat.title || "");
    setRenaming(true);
  };

  const rename = async () => {
    const target = renameValue.trim();
    const who = renameTarget;
    setRenaming(false);
    if (!target || !who) return;
    setBusy(true);
    try {
      // Rename edits the DISPLAY name (title), never the underlying slug -- so
      // nothing durable moves and grants/history stay put.
      await api.put(`/api/apps/${encodeURIComponent(who)}/title`, { title: target });
      await load();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  // Pin or unpin a conversation so the idle-expiration sweep never deletes it.
  const togglePin = async (chat) => {
    try {
      await api.put(`/api/apps/${encodeURIComponent(chat.name)}/pin`, { pinned: !chat.pinned });
      await load();
    } catch (err) {
      setError(err.message);
    }
  };

  const convert = async () => {
    if (converting) return;
    const target = (convertName || "").trim();
    if (!isValidAppName(target)) {
      setError("Enter a valid app name (letters, numbers and dashes).");
      return;
    }
    setConverting(true);
    try {
      // Convert first: it builds the app's home and unix account. Only then rename
      // to the chosen name -- renaming a lazy chat before it has an account fails.
      await api.post(`/api/apps/${encodeURIComponent(name)}/convert`, { kind: "" });
      let slug = name;
      if (target !== name) {
        const r = await api.post(`/api/apps/${encodeURIComponent(name)}/rename`, { new_name: target });
        slug = (r && r.name) || target;
      }
      refreshAccount();
      // It is an app now: it belongs in the app view, not here.
      navigate(`/app/${encodeURIComponent(slug)}`);
    } catch (err) {
      setError(err.message);
      setConverting(false);
    }
  };

  const del = () => setConfirmDelete(true);

  // A sidebar row's three-dot menu acts on THAT row's chat: for actions bound to
  // the open conversation (connections, snapshots, convert, delete), select the
  // row first, then open the sheet/dialog, which targets the now-active chat.
  const rowConvert = (chat) => { open(chat.name); setShowConvert(true); };
  const rowDelete = (chat) => { open(chat.name); setConfirmDelete(true); };

  const doDelete = async () => {
    setBusy(true);
    try {
      await api.del(`/api/apps/${encodeURIComponent(name)}`);
      refreshAccount();
      const remaining = (chats || []).filter((c) => c.name !== name);
      setChats(remaining);
      setConfirmDelete(false);
      // Move to another conversation, or the empty state.
      if (remaining.length > 0) open(remaining[0].name);
      else navigate("/chats");
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };


  // Default the convert name from the conversation's title (a real name beats the
  // "chat-xxxx" slug); fall back to the slug for an untitled chat.
  useEffect(() => {
    if (showConvert) {
      const base = activeChat && activeChat.title && activeChat.title !== "New chat" ? activeChat.title : name;
      setConvertName(filterAppName(base || ""));
    }
  }, [showConvert, activeChat, name]);

  return (
    <div className={"chats-page" + (drawerOpen ? " drawer-open" : "") + (collapsed ? " sidebar-collapsed" : "") + (active ? "" : " no-chat") + (chats === null || chats.length === 0 ? " no-chats" : "")} onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>
      {drawerOpen && <div className="chats-drawer-backdrop" onClick={() => setDrawerOpen(false)} aria-hidden="true" />}

      <div className="chats-below" ref={belowRef} style={{ "--chats-sidebar-w": `${sidebarW}px` }}>
        {!collapsed && <div className="chats-resize" onMouseDown={startResize} aria-hidden="true" />}
        <aside className="chats-sidebar">
          <div className="chats-sidebar-head">
            <button type="button" className="btn btn-primary chats-new" onClick={createChat} disabled={creating || atLimit} title={atLimit ? "You are at your conversation limit" : "Start a conversation"}>
              <NewChatIcon />
              {creating ? "Starting..." : "New chat"}
            </button>
          </div>
          <div className="chats-list">
            {chats === null ? (
              <Skeleton rows={4} label="Loading conversations..." />
            ) : chats.length === 0 ? (
              <p className="chats-empty-list">No conversations yet.</p>
            ) : (
              chats.map((c) => <ChatRow key={c.name} chat={c} active={c.name === active} working={workingNames.has(c.name)} onOpen={open} onRename={openRename} onTogglePin={togglePin} onConvert={rowConvert} onDelete={rowDelete} />)
            )}
          </div>
          <div className="chats-sidebar-foot">
            <button type="button" className="chats-sidebar-toggle" onClick={toggleSidebar} aria-label="Collapse conversations" title="Collapse sidebar">
              <SidebarToggleIcon />
            </button>
          </div>
        </aside>

        <section className="chats-main">
          <ErrorBanner message={error} onDismiss={() => setError("")} />
          {!active ? (
            <div className="chats-welcome">
              <div className="chats-welcome-icon" aria-hidden="true">
                <NewChatIcon />
              </div>
              <h2>Start a conversation</h2>
              <p>Chat with the assistant, grant it your connected accounts, and let it run things for you. Nothing gets hosted.</p>
              <button type="button" className="btn btn-primary empty-cta" onClick={createChat} disabled={creating || atLimit}>
                {creating ? "Starting..." : "New conversation"}
                <NewChatIcon />
              </button>
            </div>
          ) : (
            <div
              className={"chats-body" + (previewReady && previewOn ? " with-preview" : "")}
              ref={bodyRef}
              style={previewReady && previewOn ? { "--conv-fr": previewFrac, "--prev-fr": 1 - previewFrac } : undefined}
            >
              <div className="chats-conversation">
                <Suspense fallback={<div className="ws-chat-loading">Loading conversation...</div>}>
                  <AppAssistant
                    key={active}
                    name={active}
                    embedded
                    isChat
                    onBusyChange={setActiveBusy}
                    toolbar={
                      <>
                        {previewReady && (
                          <button type="button" className={"btn btn-icon chats-preview-toggle" + (previewOn ? " on" : "")} onClick={() => setPreviewOn((v) => !v)} aria-pressed={previewOn} title={previewOn ? "Hide preview" : "Show preview"} aria-label={previewOn ? "Hide preview" : "Show preview"}>
                            <EyeIcon />
                          </button>
                        )}
                        <ChatMenu
                          name={active}
                          busy={busy || converting}
                          pinned={activeChat.pinned}
                          onRename={() => openRename(activeChat)}
                          onTogglePin={() => togglePin(activeChat)}
                          onConvert={() => setShowConvert(true)}
                          onDelete={del}
                        />
                      </>
                    }
                    onPreviewRefresh={previewReady ? () => setPreviewNonce((n) => n + 1) : undefined}
                    onTurnComplete={() => {
                      // The server renames a "New chat" to an AI summary after its
                      // first exchange; give it a moment, then refresh the list and
                      // header to pick up the new title.
                      if (chatLabel(activeChat) === "New chat") {
                        window.setTimeout(load, 2500);
                      }
                      // A turn may have built or changed the preview.
                      window.setTimeout(() => checkPreview(active), 1200);
                      setPreviewNonce((n) => n + 1);
                    }}
                  />
                </Suspense>
              </div>
              {previewReady && previewOn && (
                <div className="chats-preview-resize" onMouseDown={startPreviewResize} aria-hidden="true" />
              )}
              {previewReady && previewOn && (
                <div className="chats-preview">
                  {/* The built page runs in an opaque origin (the endpoint sends a
                      CSP sandbox), so this iframe can never reach the dashboard's
                      cookies or DOM. The sandbox attr matches, belt and braces. */}
                  <iframe
                    key={previewNonce}
                    title="Preview"
                    className="chats-preview-frame"
                    sandbox="allow-scripts allow-popups allow-forms"
                    src={`/api/apps/${encodeURIComponent(active)}/preview/index.html?n=${previewNonce}`}
                  />
                </div>
              )}
            </div>
          )}
        </section>
      </div>

      {renaming && (
        <Sheet title="Rename conversation" onClose={() => setRenaming(false)}>
          <div className="chats-sheet-body">
            <p className="chats-modal-text">
              A display name for this conversation. It is just a label -- nothing else changes.
            </p>
            <input
              type="text"
              value={renameValue}
              onChange={(e) => setRenameValue(e.target.value)}
              placeholder="Conversation name"
              aria-label="New name"
              autoFocus
              maxLength={32}
            />
            <div className="btn-row">
              <button type="button" className="btn" onClick={() => setRenaming(false)} disabled={busy}>Cancel</button>
              <button type="button" className="btn btn-primary" onClick={rename} disabled={busy || !renameValue.trim()}>Rename</button>
            </div>
          </div>
        </Sheet>
      )}
      {showConvert && active && (
        <Sheet title="Convert to app" onClose={() => setShowConvert(false)}>
          <div className="chats-sheet-body">
            <p className="chats-modal-text">
              This turns the conversation into an app: it serves its files as a static
              site with its own subdomain and SSH, and moves to the Apps view. The
              transcript, connections and files carry over. <strong>You cannot convert it back.</strong>
            </p>
            <label className="newapp-label">App name</label>
            <div className="newapp-input">
              <span className="newapp-dollar mono">$</span>
              <input type="text" value={convertName} onChange={(e) => setConvertName(filterAppName(e.target.value))} placeholder="e.g. blog" aria-label="App name" autoFocus disabled={converting} spellCheck="false" autoComplete="off" />
            </div>
            <div className="newapp-willbe">
              <div className="row">
                <span className="ico">{"\u{1F310}"}</span>
                <span className="lab">URL</span>
                <span className="val">https://<b>{(convertName || "").replace(/[^a-z0-9-]/g, "") || "app"}</b>.{window.location.host}</span>
              </div>
              <div className="row">
                <span className="ico">{"⌨️"}</span>
                <span className="lab">SSH</span>
                <span className="val">ssh <b>{(convertName || "").replace(/[^a-z0-9-]/g, "") || "app"}</b>@{window.location.host}</span>
              </div>
            </div>
            <p className="chats-modal-text">
              It is created <strong>private</strong>, reachable only by you until you share it.
            </p>
            <div className="btn-row">
              <button type="button" className="btn" onClick={() => setShowConvert(false)} disabled={converting}>Cancel</button>
              <button type="button" className="btn btn-primary" onClick={convert} disabled={converting || !isValidAppName((convertName || "").trim())}>
                {converting ? "Converting..." : "Convert to app"}
              </button>
            </div>
          </div>
        </Sheet>
      )}
      {confirmDelete && active && (
        <ConfirmDialog
          title="Delete conversation?"
          body={`"${chatLabel(activeChat)}" and its history will be permanently removed. This cannot be undone.`}
          confirmLabel="Delete"
          danger
          busy={busy}
          onConfirm={doDelete}
          onClose={() => setConfirmDelete(false)}
        />
      )}
    </div>
  );
};

export default Chats;
